import React, { ReactChild, useState } from 'react';
import { useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { Button, ButtonContainer, Fieldset, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik } from 'formik';
import { InitialValues as FileUploadInitialValues, initialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';
import { InitialValues as IbanInitialValues } from '../iban-view/InitialValues';
import { reducedMaxFileSize } from '../../../../../../../config';
import { FileUpload as SharedFileUpload, Spinner } from '@cp-shared-19/frontend-ui';
import { mapFilesToBase64 } from '../../../../../../../utils/file-utils';
import {
    Base64File,
    formatAsFileSize,
    getChangeIbanEndpoint,
    getCheckChangeIbanEndpoint,
    IbanChange,
    IbanChangeProcessingResult,
    IbanChangeResult,
    IbanError,
    stripWhitespaces,
} from 'cpfr-common';
import { CpDataApi } from '../../../../../../../cp-xhr';
import { EditStatus, EditViewNames } from '../../enums';
import { IbanChangeNotification } from '../../iban-change-notification/IbanChangeNotification';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { Error } from '@cp-shared-19/common-utilities';
import axiosRetry from 'axios-retry';
import { useDispatch } from 'react-redux';
import { ibanStatusTagFetchStateActions } from '../../consult-view/useIbanStatusTagSlice';
import { EmailInputModal } from '../../../../../../request/form-view/email-input/EmailInputModal';
import { isDuplicateSubmissionError, useSubmitOnce } from 'utils';

type FilesUploadViewProps = {
    setCurrentView: (newEditView: EditViewNames) => void;
    onSuccess: (lastEditStatus: EditStatus, effectDate?: string) => void;
    onFail: (lastEditStatus: EditStatus) => void;
    ibanViewValues: IbanInitialValues;
    contractId: string;
};

const NUMBER_OF_RETRIES = 15;
const RETRY_DELAY = 4000;

export const FileUploadView: React.FC<FilesUploadViewProps> = ({
    setCurrentView,
    onSuccess,
    onFail,
    contractId,
    ibanViewValues,
}) => {
    const { t } = useTranslationWithFormatting(['financial-details', 'loader']);
    const { isSubmitting, run } = useSubmitOnce();
    const [lastEditStatus, setLastEditStatus] = useState<EditStatus>(EditStatus.NOT_PERFORMED);
    const [formValues, setFormValues] = useState<FileUploadInitialValues>(initialValues);
    const CpDataApiWithRetry: AxiosInstance = axios.create(CpDataApi.defaults);
    const dispatch = useDispatch();

    axiosRetry(CpDataApiWithRetry, {
        retries: NUMBER_OF_RETRIES,
        retryDelay: () => RETRY_DELAY,
        retryCondition: (error) => {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            return (error?.response?.data as any)?.code === 'REQUEST_PROCESSING_IN_PROGRESS';
        },
    });

    const prefix = 'iban-section.edit';
    const fileUploadPrefix = `${prefix}.file-upload`;

    const setFailState = (
        errorStatus: EditStatus.CHECK_FAILURE | EditStatus.BFF_API_ERROR | EditStatus.USER_WITH_NO_EMAIL_CODE,
    ): void => {
        setLastEditStatus(errorStatus);
        onFail(errorStatus);
    };

    const checkAsyncChangeIbanProcessing: (ibanProcessing: IbanChangeProcessingResult) => Promise<void> = async ({
        requestId,
    }: IbanChangeProcessingResult) => {
        const { data } = await CpDataApiWithRetry.get<IbanChangeResult>(getCheckChangeIbanEndpoint(requestId));
        if (data.message === 'REQUEST_SAVED' || data.message === 'SUCCESSFUL_REQUEST') {
            onSuccess(EditStatus.SUCCESS, data.effectDate);
            dispatch(ibanStatusTagFetchStateActions.setStatusTag(true));
        } else {
            setFailState(EditStatus.BFF_API_ERROR);
        }
    };

    // The latch covers the base64 mapping of the document too, not just the
    // request: that is where a second click used to slip through.
    const handleSubmit = (values: FileUploadInitialValues): Promise<void> =>
        run(async () => {
            setFormValues(values);
            const files: Base64File[] = await mapFilesToBase64(values.file);
            const { iban, currentIban, ibanHolder, email, allContracts, attempt } = ibanViewValues;

            return Promise.all(files).then((files) => {
                const data: IbanChange = {
                    iban: stripWhitespaces(iban),
                    currentIban,
                    ibanHolder,
                    email: values.email || email,
                    allContracts,
                    file: files[0],
                    attempt,
                };
                setLastEditStatus(EditStatus.NOT_PERFORMED);
                return CpDataApi.put(getChangeIbanEndpoint(contractId), data)
                    .then(async ({ data }: { data: IbanChangeProcessingResult }): Promise<void> => {
                        await checkAsyncChangeIbanProcessing(data);
                    })
                    .catch((error: AxiosError<Error<IbanError>>): void => {
                        // A duplicate is not a failure: the change is already registered.
                        // There is no requestId to poll for, so report it as submitted
                        // without an effect date rather than as an API error.
                        if (isDuplicateSubmissionError(error)) {
                            onSuccess(EditStatus.SUCCESS);
                            dispatch(ibanStatusTagFetchStateActions.setStatusTag(true));
                            return;
                        }

                        const code: IbanError | undefined = error.response?.data?.code;
                        const errorStatus =
                            code === 'CHECK_FAILURE'
                                ? EditStatus.CHECK_FAILURE
                                : code === EditStatus.USER_WITH_NO_EMAIL_CODE
                                ? EditStatus.USER_WITH_NO_EMAIL_CODE
                                : EditStatus.BFF_API_ERROR;

                        setLastEditStatus(errorStatus);
                        setFailState(errorStatus);
                    });
            });
        });

    return isSubmitting ? (
        <Spinner center>
            <p>{t('genericText', { ns: 'loader' })}</p>
        </Spinner>
    ) : (
        <Formik
            initialValues={formValues}
            validationSchema={getValidationSchema(t, lastEditStatus === EditStatus.USER_WITH_NO_EMAIL_CODE)}
            onSubmit={handleSubmit}
            validateOnBlur={true}
        >
            {({ handleSubmit, isValid }): ReactChild => (
                <Form onSubmit={(e): void => e.preventDefault()} data-testid="iban-form">
                    <Fieldset>
                        <Fieldset.Row>
                            <Layout>
                                <Layout.Item>
                                    <p>{t(`${fileUploadPrefix}.description`)}</p>
                                    <SharedFileUpload
                                        name={'file'}
                                        aria-required={true}
                                        uploadItemsLabel={t(`${fileUploadPrefix}.description`)}
                                        descriptionText={t(`${fileUploadPrefix}.headline`)}
                                        descriptionSupplementaryText={t(`${fileUploadPrefix}.text`)}
                                        buttonText={t(`${fileUploadPrefix}.button`)}
                                        cancelLabel={t('translation:editable-section-nav.cancel')}
                                        fileSizeFormatter={(bytes: number): string => formatAsFileSize(bytes, 2)}
                                        maxFileSize={reducedMaxFileSize}
                                        validFileFormats={['pdf']}
                                    />
                                </Layout.Item>
                            </Layout>
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <IbanChangeNotification lastEditStatus={lastEditStatus} />
                            <ButtonContainer className={'u-mt'} center>
                                <Button
                                    type="button"
                                    secondary
                                    onClick={(): void => setCurrentView(EditViewNames.IBAN_VIEW)}
                                    testId="back-to-iban-view"
                                >
                                    {t('translation:editable-section-nav.back')}
                                </Button>

                                {/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */}
                                <Button onClick={handleSubmit as any} type="button" testId="submit" disabled={!isValid}>
                                    {t('translation:editable-section-nav.submit')}
                                </Button>
                            </ButtonContainer>
                        </Fieldset.Row>
                    </Fieldset>
                    <EmailInputModal
                        name="email"
                        show={lastEditStatus === EditStatus.USER_WITH_NO_EMAIL_CODE}
                        onClose={() => setLastEditStatus(EditStatus.NOT_PERFORMED)}
                        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
                        onConfirm={handleSubmit as any}
                    />
                </Form>
            )}
        </Formik>
    );
};
